import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixLoyaltyReferralProgramV1ReferralProgramsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/referral-programs',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/referral-programs',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/referral-programs',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/referral-programs',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/referral-programs',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/referral-programs',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/referral-programs',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/referral-programs',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/referral-programs',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_referral_programs';

/** Retrieves the referral program. */
export function getReferralProgram(
  payload: object
): RequestOptionsFactory<any> {
  function __getReferralProgram({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.program',
      method: 'GET' as any,
      methodFqn:
        'wix.loyalty.referral.program.v1.ReferralPrograms.GetReferralProgram',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralProgramV1ReferralProgramsUrl({
        protoPath: '/v1/program',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'referralProgram.createdDate' },
              { path: 'referralProgram.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'referralProgram.referredFriendReward.couponOptions.minimumSubtotal',
              },
              {
                path: 'referralProgram.referredFriendReward.couponOptions.fixedAmountOptions.amount',
              },
              {
                path: 'referralProgram.referredFriendReward.couponOptions.percentageOptions.percentage',
              },
              {
                path: 'referralProgram.referringCustomerReward.couponOptions.minimumSubtotal',
              },
              {
                path: 'referralProgram.referringCustomerReward.couponOptions.fixedAmountOptions.amount',
              },
              {
                path: 'referralProgram.referringCustomerReward.couponOptions.percentageOptions.percentage',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getReferralProgram;
}

/**
 * Retrieves multiple referral programs for all metasites that the caller is the member of.
 *
 * Must be called with a [user identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#identity-types).
 */
export function bulkGetReferralProgram(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkGetReferralProgram({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.program',
      method: 'GET' as any,
      methodFqn:
        'wix.loyalty.referral.program.v1.ReferralPrograms.BulkGetReferralProgram',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralProgramV1ReferralProgramsUrl({
        protoPath: '/v1/bulk/programs',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'programInSites.referralProgram.createdDate' },
              { path: 'programInSites.referralProgram.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'programInSites.referralProgram.referredFriendReward.couponOptions.minimumSubtotal',
              },
              {
                path: 'programInSites.referralProgram.referredFriendReward.couponOptions.fixedAmountOptions.amount',
              },
              {
                path: 'programInSites.referralProgram.referredFriendReward.couponOptions.percentageOptions.percentage',
              },
              {
                path: 'programInSites.referralProgram.referringCustomerReward.couponOptions.minimumSubtotal',
              },
              {
                path: 'programInSites.referralProgram.referringCustomerReward.couponOptions.fixedAmountOptions.amount',
              },
              {
                path: 'programInSites.referralProgram.referringCustomerReward.couponOptions.percentageOptions.percentage',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkGetReferralProgram;
}

/**
 * Updates a referral program. Supports partial updates.
 *
 * Revision number, which increments by 1 each time the referral program is updated.
 * To prevent conflicting changes, the current revision must be passed when updating the referral program.
 */
export function updateReferralProgram(
  payload: object
): RequestOptionsFactory<any> {
  function __updateReferralProgram({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'mask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'referralProgram.createdDate' },
          { path: 'referralProgram.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'referralProgram.referredFriendReward.couponOptions.minimumSubtotal',
          },
          {
            path: 'referralProgram.referredFriendReward.couponOptions.fixedAmountOptions.amount',
          },
          {
            path: 'referralProgram.referredFriendReward.couponOptions.percentageOptions.percentage',
          },
          {
            path: 'referralProgram.referringCustomerReward.couponOptions.minimumSubtotal',
          },
          {
            path: 'referralProgram.referringCustomerReward.couponOptions.fixedAmountOptions.amount',
          },
          {
            path: 'referralProgram.referringCustomerReward.couponOptions.percentageOptions.percentage',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.program',
      method: 'PATCH' as any,
      methodFqn:
        'wix.loyalty.referral.program.v1.ReferralPrograms.UpdateReferralProgram',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralProgramV1ReferralProgramsUrl({
        protoPath: '/v1/program',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'referralProgram.createdDate' },
              { path: 'referralProgram.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'referralProgram.referredFriendReward.couponOptions.minimumSubtotal',
              },
              {
                path: 'referralProgram.referredFriendReward.couponOptions.fixedAmountOptions.amount',
              },
              {
                path: 'referralProgram.referredFriendReward.couponOptions.percentageOptions.percentage',
              },
              {
                path: 'referralProgram.referringCustomerReward.couponOptions.minimumSubtotal',
              },
              {
                path: 'referralProgram.referringCustomerReward.couponOptions.fixedAmountOptions.amount',
              },
              {
                path: 'referralProgram.referringCustomerReward.couponOptions.percentageOptions.percentage',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateReferralProgram;
}

/** Activates the referral program, changing its status to `ACTIVE`. */
export function activateReferralProgram(
  payload: object
): RequestOptionsFactory<any> {
  function __activateReferralProgram({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.program',
      method: 'PATCH' as any,
      methodFqn:
        'wix.loyalty.referral.program.v1.ReferralPrograms.ActivateReferralProgram',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralProgramV1ReferralProgramsUrl({
        protoPath: '/v1/program/activate',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'referralProgram.createdDate' },
              { path: 'referralProgram.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'referralProgram.referredFriendReward.couponOptions.minimumSubtotal',
              },
              {
                path: 'referralProgram.referredFriendReward.couponOptions.fixedAmountOptions.amount',
              },
              {
                path: 'referralProgram.referredFriendReward.couponOptions.percentageOptions.percentage',
              },
              {
                path: 'referralProgram.referringCustomerReward.couponOptions.minimumSubtotal',
              },
              {
                path: 'referralProgram.referringCustomerReward.couponOptions.fixedAmountOptions.amount',
              },
              {
                path: 'referralProgram.referringCustomerReward.couponOptions.percentageOptions.percentage',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __activateReferralProgram;
}

/** Pauses the referral program, changing its status to `PAUSED`. */
export function pauseReferralProgram(
  payload: object
): RequestOptionsFactory<any> {
  function __pauseReferralProgram({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.program',
      method: 'PATCH' as any,
      methodFqn:
        'wix.loyalty.referral.program.v1.ReferralPrograms.PauseReferralProgram',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralProgramV1ReferralProgramsUrl({
        protoPath: '/v1/program/pause',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'referralProgram.createdDate' },
              { path: 'referralProgram.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'referralProgram.referredFriendReward.couponOptions.minimumSubtotal',
              },
              {
                path: 'referralProgram.referredFriendReward.couponOptions.fixedAmountOptions.amount',
              },
              {
                path: 'referralProgram.referredFriendReward.couponOptions.percentageOptions.percentage',
              },
              {
                path: 'referralProgram.referringCustomerReward.couponOptions.minimumSubtotal',
              },
              {
                path: 'referralProgram.referringCustomerReward.couponOptions.fixedAmountOptions.amount',
              },
              {
                path: 'referralProgram.referringCustomerReward.couponOptions.percentageOptions.percentage',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __pauseReferralProgram;
}

/**
 * Retrieves pre-generated AI social media post suggestions for promoting the referral program.
 *
 * This method returns a list of AI-generated social media post suggestions that site owners or members can use to promote the referral program. You can display these suggestions to allow referring customers to easily copy and share them on their preferred social media platforms.
 *
 * >**Note**: This method retrieves existing suggestions. To generate new ones,
 * use Generate AI Social Media Posts Suggestions.
 */
export function getAiSocialMediaPostsSuggestions(
  payload: object
): RequestOptionsFactory<any> {
  function __getAiSocialMediaPostsSuggestions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.program',
      method: 'GET' as any,
      methodFqn:
        'wix.loyalty.referral.program.v1.ReferralPrograms.GetAISocialMediaPostsSuggestions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralProgramV1ReferralProgramsUrl({
        protoPath: '/v1/program/ai-social-media-posts-suggestions',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getAiSocialMediaPostsSuggestions;
}

/**
 * Creates new AI-generated social media post suggestions for promoting the referral program.
 *
 * This method generates new AI-powered social media post suggestions for promoting the referral program. Use it to refresh content or create alternatives to existing suggestions.
 *
 * >**Note**: This method generates new suggestions each time it's called. To retrieve existing suggestions without generating new ones, use Get AI Social Media Posts Suggestions.
 */
export function generateAiSocialMediaPostsSuggestions(
  payload: object
): RequestOptionsFactory<any> {
  function __generateAiSocialMediaPostsSuggestions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.program',
      method: 'POST' as any,
      methodFqn:
        'wix.loyalty.referral.program.v1.ReferralPrograms.GenerateAISocialMediaPostsSuggestions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralProgramV1ReferralProgramsUrl({
        protoPath: '/v1/program/ai-social-media-posts-suggestions',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __generateAiSocialMediaPostsSuggestions;
}

/** Retrieves information about the enabled premium features for the referral program. */
export function getReferralProgramPremiumFeatures(
  payload: object
): RequestOptionsFactory<any> {
  function __getReferralProgramPremiumFeatures({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.program',
      method: 'GET' as any,
      methodFqn:
        'wix.loyalty.referral.program.v1.ReferralPrograms.GetReferralProgramPremiumFeatures',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralProgramV1ReferralProgramsUrl({
        protoPath: '/v1/program/premium-features',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getReferralProgramPremiumFeatures;
}
